/**
 * hook for deleting pages from a metadoc.
 * manages request kickoff and emits operation lifecycle events to parent.
 */
import { KaboodleToaster } from "components/common";
import { useState } from "react";
import { useDeletePagesFromMetadocMutation } from "services/metadoc-api";

import { logMetadocDebug } from "../../utils/metadocDebugLogger";
import { convertToPageRanges } from "../../utils/pageRangeUtils";

import type { GroupingSource } from "services/metadoc-api";
import type { PageState } from "../../metadoc.types";

type UsePageDeletionParams = {
  metadocId?: number;
  versionHash?: string | null;
  groupingSource?: GroupingSource;
  currentPage: number;
  selectedPages: Set<number>;
  onClearSelection: () => void;
  onSetPageStates?: (states: Map<number, PageState>) => void;
  onDeletionStateChange?: (
    isDeletingPages: boolean,
    pageCount: number,
    pageRanges: number[][],
  ) => void;
  onDeletionOperationStart?: (meta: {
    pageCount: number;
    pageRanges: number[][];
    cursorPositionBeforeDeletion: number;
  }) => void;
  onDeletionOperationJobCreated?: (jobId: number) => void;
  onDeletionOperationFail?: (statusMessage?: string) => void;
  onDeletionOperationReset?: () => void;
};

type UsePageDeletionReturn = {
  isDeletingPages: boolean;
  isDeleteModalOpen: boolean;
  openDeleteModal: () => void;
  closeDeleteModal: () => void;
  handleDeleteConfirm: () => Promise<void>;
};

export const usePageDeletion = ({
  metadocId,
  versionHash,
  groupingSource = "OPS",
  currentPage,
  selectedPages,
  onClearSelection,
  onSetPageStates,
  onDeletionStateChange,
  onDeletionOperationStart,
  onDeletionOperationJobCreated,
  onDeletionOperationFail,
  onDeletionOperationReset,
}: UsePageDeletionParams): UsePageDeletionReturn => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePagesFromMetadoc, { isLoading: isDeleteRequestLoading }] =
    useDeletePagesFromMetadocMutation();

  const isDeletingPages = !!isDeleteRequestLoading;

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!metadocId || !versionHash) {
      return;
    }

    // close modal first (before clearing selection so modal can show correct count)
    setIsDeleteModalOpen(false);

    const pageRanges = convertToPageRanges(selectedPages);

    if (onSetPageStates) {
      const deletionStates = new Map<number, PageState>();
      for (const pageNum of selectedPages) {
        deletionStates.set(pageNum, "deleting");
      }
      onSetPageStates(deletionStates);
    }

    onDeletionStateChange?.(true, selectedPages.size, pageRanges);
    onDeletionOperationStart?.({
      pageCount: selectedPages.size,
      pageRanges,
      cursorPositionBeforeDeletion: currentPage,
    });

    onClearSelection();

    try {
      logMetadocDebug("page deletion requested", {
        metadocId,
        selectedPageCount: selectedPages.size,
        pageRanges,
        groupingSource,
      });
      const response = await deletePagesFromMetadoc({
        metadocId,
        pageRanges,
        versionHash,
        source: groupingSource,
      }).unwrap();

      logMetadocDebug("page deletion background job created", {
        metadocId,
        jobId: response.background_job.id,
      });
      onDeletionOperationJobCreated?.(response.background_job.id);
    } catch (error) {
      logMetadocDebug("page deletion request failed", {
        metadocId,
      });

      onDeletionStateChange?.(false, 0, []);
      onDeletionOperationReset?.();

      if (onSetPageStates) {
        onSetPageStates(new Map());
      }

      const errorStatus = (error as { status?: number })?.status;
      if (errorStatus === 409) {
        KaboodleToaster.error("Version mismatch. Please reload the page.");
        onDeletionOperationFail?.("Version mismatch. Please reload the page.");
      } else {
        KaboodleToaster.error("Failed to delete pages");
        onDeletionOperationFail?.("Failed to delete pages");
      }
    }
  };

  return {
    isDeletingPages,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteConfirm,
  };
};
